import config from "console_config";
import logger from "Libs/logger";
import { request } from "Libs/platform";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { projectCreationActions } from "../projectCreation.reducer";
import { errorTypes } from "../projectCreation.types";

const githubAppEndpoint = `${config.URL_API_PROXY}/gh`;

export type CreateProjectFromGithubProps = {
  organizationId: string;
  installationId: string;
  repositoryName: string;
  projectTitle: string;
  projectRegion: string;
  defaultBranch: string;
};

export type CreateProjectFromGithubResult = {
  project_id?: string;
  data?: {
    project_id: string;
  };
};

/**
 * Creates a project from a GitHub repository.
 *
 * This thunk creates a new project by importing from a GitHub repository
 * through the GitHub App integration. It handles the initial creation request
 * and sets up the project creation state for subsequent polling.
 */
export const createProjectFromGithub = createAppAsyncThunk(
  "onboarding/createProjectFromGithub",
  async (
    {
      organizationId,
      installationId,
      repositoryName,
      projectTitle,
      projectRegion,
      defaultBranch
    }: CreateProjectFromGithubProps,
    { dispatch, rejectWithValue }
  ) => {
    dispatch(
      projectCreationActions.setProjectCreationLoading({
        organizationId,
        isLoading: true
      })
    );

    // Clear any previous errors
    dispatch(
      projectCreationActions.clearProjectCreationError({ organizationId })
    );

    try {
      const response = (await request(
        `${githubAppEndpoint}/${organizationId}/orgs/${installationId}/repos/${repositoryName}`,
        "POST",
        {
          project_region: projectRegion,
          project_title: projectTitle,
          default_branch: defaultBranch
        }
      )) as CreateProjectFromGithubResult;

      const projectId = response.data?.project_id ?? response.project_id;

      dispatch(
        projectCreationActions.setProjectCreationLoading({
          organizationId,
          isLoading: false
        })
      );

      return { projectId, response };
    } catch (err: unknown) {
      const error = err instanceof Error ? err.message : "An error occurred";

      // Try to parse error details
      let errorDetail = error;
      try {
        if (typeof err === "string") {
          const parsed = JSON.parse(err);
          errorDetail =
            parsed.title ?? parsed.detail ?? parsed.message ?? error;
        } else if (err && typeof err === "object") {
          const errObj = err as {
            title?: string;
            detail?: string;
            message?: string;
          };
          errorDetail =
            errObj.title ?? errObj.detail ?? errObj.message ?? error;
        }
      } catch {
        // Keep original error
      }

      logger(err, {
        action: "createProjectFromGithub",
        organizationId,
        installationId,
        repositoryName
      });

      dispatch(
        projectCreationActions.setProjectCreationError({
          organizationId,
          errorType: errorTypes.custom
        })
      );

      return rejectWithValue({ error: errorDetail });
    }
  }
);
